/**
 * product-api のサービス間呼び出し契約（案件起票 + プロジェクト管理）。
 *
 * product-api（`apps/api/product/src/features/project-intake` / `features/project`）と、
 * それを代理実行する mcp-project-management（`apps/mcp/project-management-server`）の
 * **両端が import する唯一の定義**。パッケージ名は最初の利用者が起票だった経緯で
 * `project-intake-contract` のままだが、対象は product-api へのサービス間経路全体。
 *
 * audience / ヘッダ名 / path をこの 1 箇所に閉じるのは、ずれても **型エラーにも deploy 失敗にも
 * ならず、本番で常時 403 / 404 になるだけ**だから。cortex は Cloud Scheduler の `oidcToken.audience`
 * 未指定で「本番稼働以来 1 度も成功していなかった」事故を既に踏んでいる（docs/guidelines/
 * cloud-run-deploy.md）。`apps/` 間の直接 import は不可なので、共有はこのパッケージで行う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 案件起票 API のサービス間呼び出し契約（OIDC audience・代理実行ヘッダ・path・payload 型）
 * @graph-connects none
 */
/**
 * サービス間 OIDC 呼び出しで使う audience。
 *
 * 呼び出し側はこの値を metadata server の identity エンドポイントに渡して ID token を発行し、
 * product-api は同じ値で `verifyIdToken` する。**Cloud Run の URL を使わない**のは、
 * product-api の URL が自分自身の env に注入できない（Pulumi の循環参照になる）ため。
 * 宛先ごとに閉じた固定文字列にして、別サービス宛に発行した token の使い回しを防ぐ。
 *
 * 粒度は **宛先サービス単位**（product-api）であって機能単位ではない。同一サービス内の
 * 別 path に別 audience を割り当てても、検証する側も allowlist も同じなので安全性は増えない。
 * 値が `:project-intake` で終わるのは最初の利用者が起票だったという歴史的経緯で、
 * **意図的に据え置いている**: 値を変えると product-api と mcp-project-management が別
 * Pulumi stack（デプロイは api-product → mcp-project-management の順で 10 分前後ずれる）
 * である以上、その窓で必ず 403 が出る。名前が実態を表すよう、シンボル名だけを改めた。
 * @graph-connects none
 */
export const PRODUCT_API_SERVICE_AUDIENCE = 'cortex-service:product-api:project-intake';
/**
 * サービス間呼び出しで「誰の代理で起票するか」を渡すヘッダ。
 * Edge Auth の `X-Cortex-User-Email` とは別名にして、Edge が注入した identity と
 * サービス呼び出し元が主張する identity を取り違えないようにする。
 * @graph-connects none
 */
export const ACTING_USER_HEADER = 'X-Cortex-Acting-User';
/** 起票フォームの選択肢取得 path（GET）。 @graph-connects none */
export const PROJECT_INTAKE_OPTIONS_PATH = '/project-intake/options';
/** 起票実行 path（POST）。 @graph-connects none */
export const PROJECT_INTAKE_CREATE_PATH = '/project-intake/projects';
/**
 * 「実起票に必要なサーバー側の外部連携設定（Slack / GAS の Secret・env）が未投入」で
 * product-api が実起票を拒否したことを表す、機械可読なエラーコード。
 *
 * product-api（`assertExternalConfig`）がこのコードを `error.code` に載せて 400 を返し、
 * mcp-project-management（`extractErrorMessage`）はこのコードのときだけ利用者向けの固定文面へ
 * 差し替える。こうして原文に含まれる env / Secret 変数名（`SLACK_BOT_TOKEN` 等）を AI エージェント
 * へ渡さず、「利用者に API キーを要求する」誤誘導も止める。
 *
 * 判定を**コード**で揃えるのは、以前の「自由文を正規表現で判定」が本番の実文言（例:「未構成」）と
 * ずれて漏れていたため。文面ではなくこのマーカーを唯一の契約にすることで、文言が変わってもずれない。
 * @graph-connects none
 */
export const EXTERNAL_CONFIG_UNSET_ERROR_CODE = 'PROJECT_INTAKE_EXTERNAL_CONFIG_UNSET';
/** 軽タスクの共有プール取得 path（GET）。 @graph-connects none */
export const LIGHT_TASK_POOL_PATH = '/projects/light-tasks';
/** アサイン済み軽タスク一覧の取得 path（GET）。 @graph-connects none */
export const LIGHT_TASK_ASSIGNED_PATH = '/projects/light-tasks/assigned';
/** 軽タスクのセルフ・アサイン path（POST・body 無し）。 @graph-connects none */
export const LIGHT_TASK_CLAIM_PATH = '/projects/light-tasks/claim';
/** PMO 看板の全ビュー（MCP ツールの enum 生成に使う）。 @graph-connects none */
export const PMO_BOARD_VIEWS = [
    'overdue',
    'upcoming',
    'release-leaks',
    'assign-leaks',
];
/**
 * PMO 看板の各ビュー取得 path（GET）。
 * @graph-connects none
 */
export function pmoBoardPath(view) {
    return `/projects/pmo-board/${view}`;
}
/**
 * PMO 看板からの期限更新 path（PATCH）。Backlog 本体へ書き戻す。
 * @graph-connects none
 */
export function projectDeadlinesPath(issueKey) {
    return `/projects/${encodeURIComponent(issueKey)}/deadlines`;
}
/** バグ評価一覧の取得 path（GET）。 @graph-connects none */
export const BUG_EVALUATIONS_PATH = '/bug-evaluations';
/** バグ評価のレビュー更新 path（PUT）。 @graph-connects none */
export function bugEvaluationReviewPath(issueKey) {
    return `/bug-evaluations/${encodeURIComponent(issueKey)}`;
}
/**
 * 未評価バグ（一次判定の待ち行列）の取得 path（GET）。
 *
 * `BUG_EVALUATIONS_PATH` が返すのは**既に評価が付いたもの**で、`reviewStatus=unreviewed`
 * は「AI が評価したが人が確認していない」を意味する。一次判定そのものが済んでいない
 * バグはどちらにも出てこないため、別の口にしている。
 * @graph-connects none
 */
export const BUG_EVALUATION_QUEUE_PATH = '/bug-evaluations/queue';
/**
 * バグ評価の原因分析 path（GET）。
 *
 * 分類項目ごとの件数・i-point 合計、月次の推移、代表的な理由文を返す。
 * 開発リーダー等の個人名は返さない（チーム単位まで）。
 * @graph-connects none
 */
export const BUG_EVALUATION_ANALYSIS_PATH = '/bug-evaluations/analysis';
/**
 * AI 一次判定の提出 path（POST）。
 *
 * 人間レビューの確定口（`bugEvaluationReviewPath` の PUT）は使えない。あちらは
 * `admin` / `architect` / `tech_lead` の PortalRole を要求し、保存すると
 * `isHumanReviewed` が立つ＝人が確認した扱いになる。一次判定は bot のサービス
 * アカウントが出し、人の確認はまだ済んでいないので、別の口にして許可リストで絞る。
 * @graph-connects none
 */
export function bugEvaluationAiEvaluationPath(issueKey) {
    return `/bug-evaluations/${encodeURIComponent(issueKey)}/ai-evaluation`;
}
/**
 * 起因案件の実装担当者の候補の取得 path（GET）。
 *
 * 候補は一次判定の提出（`bugEvaluationAiEvaluationPath`）に同梱されて入る。読み出し時に
 * `users` を見てニックネームを当てるため、提出された名前をそのまま返す経路は無い。
 * @graph-connects none
 */
export function bugEvaluationImplementerCandidatesPath(issueKey) {
    return `/bug-evaluations/${encodeURIComponent(issueKey)}/implementer-candidates`;
}
/**
 * i-point 一次判定の文脈の取得 path（GET）。
 *
 * **評価がまだ無い課題でも引ける。** 評価詳細（`/bug-evaluations/:issueKey`）は
 * `bug_evaluations` を起点に join するため評価が無いと 1 行も返らず、一次判定は定義上
 * 「評価がまだ無い課題」に対して行うので、判定側はこちらを使う。
 * @graph-connects none
 */
export function bugEvaluationJudgmentContextPath(issueKey) {
    return `/bug-evaluations/${encodeURIComponent(issueKey)}/context`;
}
/**
 * AI 一次判定の下書き取得 path（GET）。
 *
 * 既に評価が付いているバグへ一次判定が来た場合（Gemini 併走中・人が確定済み）は
 * 本体を上書きせず下書きへ退避する。その退避分を読んで突き合わせるための口。
 * @graph-connects none
 */
export function bugEvaluationAiDraftPath(issueKey) {
    return `/bug-evaluations/${encodeURIComponent(issueKey)}/ai-draft`;
}
/**
 * 起因案件の付け替え path（PUT）。
 *
 * **評価の確定（`bugEvaluationReviewPath`）とは別の口にする。** 起因案件はバグチケット側の
 * 事実で、評価が付く前から設定されている（むしろ評価の入力になる）。確定の口に相乗りさせると
 * 付け替えただけで `isHumanReviewed` が立ち、レビュー待ち一覧から消える。
 * @graph-connects none
 */
export function bugEvaluationCauseProjectPath(issueKey) {
    return `/bug-evaluations/${encodeURIComponent(issueKey)}/cause-project`;
}
/**
 * 起因案件の付け替え先候補の取得 path（GET）。
 *
 * 現在の起因案件とその課題種別、置けない種別だった場合の候補を返す。書ける口だけ作ると
 * 「弾かれたが次に何を入れればいいか分からない」で手が止まる。
 * @graph-connects none
 */
export function bugEvaluationCauseProjectCandidatesPath(issueKey) {
    return `/bug-evaluations/${encodeURIComponent(issueKey)}/cause-project/candidates`;
}
/**
 * 見送られた一次判定の一覧 path（GET）。
 *
 * **見送りは評価本体を持たないので、評価一覧（`/bug-evaluations`）には 1 件も出てこない。**
 * 課題キーを知らないと `ai-draft` も引けないため、「どのバグが一次判定で止まっているか」を
 * 課題キーを知らずに読める口がここだけになる。
 * @graph-connects none
 */
export const BUG_EVALUATION_AI_DRAFTS_PATH = '/bug-evaluations/ai-drafts';
/** QA責任有無一覧の取得 path（GET）。 @graph-connects none */
export const QA_RESPONSIBILITIES_PATH = '/qa-responsibilities';
/** QA責任有無の確定 path（PUT）。 @graph-connects none */
export function qaResponsibilityConfirmPath(issueKey) {
    return `/qa-responsibilities/${encodeURIComponent(issueKey)}`;
}
/**
 * Gen2 案件の子チケット一覧取得 path（GET）。
 * 実績pt はこの子チケット単位で入力し、集計されて案件の実績になる。
 * @graph-connects none
 */
export function projectChildrenPath(projectId) {
    return `/projects/${String(projectId)}/children`;
}
/**
 * 打ち合わせの種類を表す固定タグ一覧。
 *
 * Smart Notes(議事録)取得時にGeminiが本文から該当するものを分類し（0件以上、複数可）、
 * 人手での上書きも可能。新しいタグを増やす場合はこの配列に追記する。
 *
 * ここが唯一の定義元: `@cortex/domain-meet`（Firestore 等 Node 専用依存を持ち web から
 * 直接 import できない）はこの値を re-export し、`apps/web/product-innovation-lab` は
 * ここから直接 import する。依存の無いこのパッケージに置くことで、バックエンド・MCP・Web
 * 全員が同じ配列を単一の情報源として参照できる。
 * @graph-connects none
 */
export const MEETING_TAGS = [
    '要件定義インプット',
    'デザインインプット',
    '開発インプット',
    'QAインプット',
    '要件定義相談',
    'システム仕様相談',
    'デザイン相談',
    'QA相談',
    '基本設計相談',
    '開発相談',
    '結合テスト相談',
    '総合テスト相談',
    'その他相談',
];
/**
 * 案件の打ち合わせ一覧取得（GET）・作成（POST） path。
 * Slack bot（ai-fassy）・Chrome拡張（meet-calendar）と同じ機構で Meet を作成する。
 * `tags` を渡すと、そのいずれかのタグを持つ打ち合わせだけに絞り込むクエリを付ける（GET専用）。
 * @graph-connects none
 */
export function projectMeetingsPath(pjKey, tags) {
    const base = `/projects/${encodeURIComponent(pjKey)}/meetings`;
    if (tags === undefined || tags.length === 0)
        return base;
    return `${base}?tags=${encodeURIComponent(tags.join(','))}`;
}
/**
 * 打ち合わせ1件の詳細（文字起こし・議事録）取得 path（GET）。
 * @graph-connects none
 */
export function projectMeetingDetailPath(pjKey, meetingId) {
    return `/projects/${encodeURIComponent(pjKey)}/meetings/${encodeURIComponent(meetingId)}`;
}
/**
 * 実績pt（Backlog の実績時間）更新 path（PATCH）。
 * @graph-connects none
 */
export function projectActualHoursPath(issueKey) {
    return `/projects/${encodeURIComponent(issueKey)}/actual-hours`;
}
/**
 * 会話から指定できる Backlog ステータスと、Backlog 側の statusId のマッピング（SoT）。
 * 値は本番 `backlog_no_embedding.issues_normalized` の `status_id` / `status` を実測して確定した
 * （処理中=2 / 完了=4）。product-api（statusId 解決）・mcp-project-management（enum 生成）の
 * 両端がこの 1 箇所から `BacklogIssueStatus` / `BACKLOG_ISSUE_STATUSES` を導出する。
 * @graph-connects none
 */
export const BACKLOG_ISSUE_STATUS_IDS = {
    処理中: 2,
    完了: 4,
};
/** 指定できるステータスの全値（MCP ツールの enum 生成に使う）。 @graph-connects none */
export const BACKLOG_ISSUE_STATUSES = Object.keys(BACKLOG_ISSUE_STATUS_IDS);
/**
 * 許可根拠の日本語表示名（MCP ツールの出力・403 の説明文で使う）。
 * @graph-connects none
 */
export const ISSUE_OWNERSHIP_LABELS = {
    assignee: '担当者本人',
    'dev-leader': '開発リーダー',
    'parent-pl': '母票の PL',
    'parent-main-worker': '母票の主担当',
};
/**
 * 開発チケットのステータス変更 path（PATCH）。
 * @graph-connects none
 */
export function projectIssueStatusPath(issueKey) {
    return `/projects/${encodeURIComponent(issueKey)}/status`;
}
/** 案件種類の全値（MCP ツールの enum 生成に使う）。 @graph-connects none */
export const PROJECT_INTAKE_KINDS = [
    '開発のみ',
    'デザインのみ',
    '両方',
    '軽タスク',
];
export * from './assignment-history.js';
export * from './project-rename.js';
export * from './project-deliverable-folders.js';
export * from './project-issues.js';
//# sourceMappingURL=index.js.map